/**
 * 装备模板数据（50+件）
 */

export type Quality = 'common' | 'spirit' | 'mystic' | 'earth' | 'heaven' | 'immortal';
export type EquipSlot = 'head' | 'face' | 'neck' | 'body' | 'inner' | 'back' | 'arms' | 'hands' | 'ring1' | 'ring2' | 'waist' | 'legs' | 'feet' | 'weapon' | 'offhand';

export interface StatRange {
  attack?: [number, number];
  defense?: [number, number];
  hp?: [number, number];
  mp?: [number, number];
}

export interface EquipTemplate {
  id: string;
  name: string;
  slot: EquipSlot;
  quality: Quality;
  baseStats: StatRange;
  capacity?: number;
  price: number;
  desc: string;
}

export const QUALITY_MULT: Record<Quality, number> = {
  common: 1.0, spirit: 1.5, mystic: 2.2, earth: 3.0, heaven: 4.0, immortal: 6.0,
};

export const QUALITY_NAMES: Record<Quality, string> = {
  common: '凡品', spirit: '灵品', mystic: '玄品', earth: '地品', heaven: '天品', immortal: '仙品',
};

export const SLOT_NAMES: Record<EquipSlot, string> = {
  head: '头冠', face: '面饰', neck: '项链', body: '道袍', inner: '内甲', back: '披风',
  arms: '护臂', hands: '手套', ring1: '戒指1', ring2: '戒指2',
  waist: '腰带', legs: '护腿', feet: '战靴', weapon: '武器', offhand: '副手',
};

export const SLOT_ICONS: Record<EquipSlot, string> = {
  head: '🎩', face: '🎭', neck: '📿', body: '👘', inner: '🦺', back: '🧣',
  arms: '💪', hands: '🧤', ring1: '💍', ring2: '💍',
  waist: '🎒', legs: '🦵', feet: '👢', weapon: '🗡️', offhand: '🛡️',
};

export const EQUIP_TEMPLATES: EquipTemplate[] = [
  // === 头冠 ===
  { id: 'cloth_hat', name: '布巾', slot: 'head', quality: 'common', baseStats: { mp: [5, 15] }, price: 30, desc: '最基础的头巾' },
  { id: 'jade_crown', name: '青玉冠', slot: 'head', quality: 'spirit', baseStats: { mp: [20, 45] }, price: 120, desc: '温润青玉所制' },
  { id: 'gold_crown', name: '紫金道冠', slot: 'head', quality: 'mystic', baseStats: { mp: [50, 100] }, price: 400, desc: '紫金打造的道冠' },
  { id: 'void_crown', name: '太虚法冠', slot: 'head', quality: 'heaven', baseStats: { mp: [150, 300] }, price: 0, desc: '虚无中诞生的法冠' },

  // === 面饰 ===
  { id: 'cloth_mask', name: '面巾', slot: 'face', quality: 'common', baseStats: { defense: [1, 3] }, price: 20, desc: '遮风挡尘' },
  { id: 'spirit_veil', name: '灵雾面纱', slot: 'face', quality: 'spirit', baseStats: { defense: [4, 10] }, price: 100, desc: '灵雾凝成的面纱' },
  { id: 'demon_mask', name: '修罗面具', slot: 'face', quality: 'mystic', baseStats: { defense: [12, 28], attack: [3, 8] }, price: 350, desc: '修罗的面容' },

  // === 项链 ===
  { id: 'rope_neck', name: '麻绳', slot: 'neck', quality: 'common', baseStats: { hp: [5, 15] }, price: 25, desc: '粗糙的麻绳' },
  { id: 'jade_pendant', name: '玉佩', slot: 'neck', quality: 'spirit', baseStats: { hp: [20, 50] }, price: 150, desc: '温养经脉的玉佩' },
  { id: 'dragon_heart', name: '龙心坠', slot: 'neck', quality: 'mystic', baseStats: { hp: [60, 140] }, price: 500, desc: '龙血结晶所铸' },
  { id: 'phoenix_tear', name: '凤凰泪', slot: 'neck', quality: 'heaven', baseStats: { hp: [180, 400] }, price: 0, desc: '凤凰泣血所化' },

  // === 道袍 ===
  { id: 'cloth_robe', name: '粗布衣', slot: 'body', quality: 'common', baseStats: { defense: [2, 6] }, price: 40, desc: '粗布缝制' },
  { id: 'spirit_robe', name: '灵丝道袍', slot: 'body', quality: 'spirit', baseStats: { defense: [8, 20] }, price: 200, desc: '灵丝织就' },
  { id: 'silkworm_robe', name: '天蚕玄衣', slot: 'body', quality: 'mystic', baseStats: { defense: [25, 55] }, price: 600, desc: '天蚕丝所制' },
  { id: 'chaos_robe', name: '混元仙袍', slot: 'body', quality: 'heaven', baseStats: { defense: [70, 150], hp: [50, 120] }, price: 0, desc: '混沌中织就的仙袍' },

  // === 内甲 ===
  { id: 'cloth_inner', name: '麻布内衬', slot: 'inner', quality: 'common', baseStats: { hp: [5, 12] }, price: 35, desc: '最基础的内衬' },
  { id: 'silk_inner', name: '灵丝软甲', slot: 'inner', quality: 'spirit', baseStats: { hp: [15, 40] }, price: 180, desc: '灵丝编织的软甲' },
  { id: 'scale_inner', name: '龙鳞内甲', slot: 'inner', quality: 'mystic', baseStats: { hp: [50, 110], defense: [5, 12] }, price: 550, desc: '龙鳞所制' },

  // === 披风 ===
  { id: 'cloth_cloak', name: '麻布披风', slot: 'back', quality: 'common', baseStats: { defense: [1, 3] }, price: 25, desc: '遮风挡雨' },
  { id: 'wind_cloak', name: '御风披风', slot: 'back', quality: 'spirit', baseStats: { defense: [5, 12] }, price: 130, desc: '御风而行' },
  { id: 'star_cloak', name: '星辰斗篷', slot: 'back', quality: 'mystic', baseStats: { defense: [15, 35], hp: [20, 50] }, price: 450, desc: '星光织就' },

  // === 护臂 ===
  { id: 'cloth_arms', name: '布护臂', slot: 'arms', quality: 'common', baseStats: { attack: [1, 4] }, price: 30, desc: '基础护臂' },
  { id: 'iron_arms', name: '铁护臂', slot: 'arms', quality: 'spirit', baseStats: { attack: [5, 15] }, price: 140, desc: '精铁打造' },
  { id: 'dragon_arms', name: '龙力护臂', slot: 'arms', quality: 'mystic', baseStats: { attack: [18, 42] }, price: 480, desc: '蕴含龙力' },

  // === 手套 ===
  { id: 'cloth_gloves', name: '布手套', slot: 'hands', quality: 'common', baseStats: { attack: [1, 3] }, price: 20, desc: '基础手套' },
  { id: 'spirit_gloves', name: '灵丝手套', slot: 'hands', quality: 'spirit', baseStats: { attack: [4, 12] }, price: 110, desc: '灵丝编织' },
  { id: 'flame_gloves', name: '烈焰手', slot: 'hands', quality: 'mystic', baseStats: { attack: [14, 35] }, price: 380, desc: '蕴含火属灵力' },

  // === 护腿 ===
  { id: 'cloth_legs', name: '布护腿', slot: 'legs', quality: 'common', baseStats: { defense: [1, 4], hp: [3, 8] }, price: 30, desc: '基础护腿' },
  { id: 'iron_legs', name: '精铁护腿', slot: 'legs', quality: 'spirit', baseStats: { defense: [5, 14], hp: [8, 20] }, price: 150, desc: '精铁打造' },
  { id: 'cloud_legs', name: '踏云护腿', slot: 'legs', quality: 'mystic', baseStats: { defense: [18, 40], hp: [20, 50] }, price: 420, desc: '轻如云雾' },

  // === 战靴 ===
  { id: 'cloth_boots', name: '布鞋', slot: 'feet', quality: 'common', baseStats: { defense: [1, 3] }, price: 25, desc: '最基础的鞋子' },
  { id: 'wind_boots', name: '疾风靴', slot: 'feet', quality: 'spirit', baseStats: { defense: [4, 10] }, price: 120, desc: '疾风加持' },
  { id: 'cloud_boots', name: '踏云靴', slot: 'feet', quality: 'mystic', baseStats: { defense: [12, 28] }, price: 350, desc: '可短暂踏空而行' },

  // === 武器 ===
  { id: 'iron_sword', name: '铁剑', slot: 'weapon', quality: 'common', baseStats: { attack: [3, 8] }, price: 50, desc: '普通铁剑' },
  { id: 'spirit_sword', name: '灵剑', slot: 'weapon', quality: 'spirit', baseStats: { attack: [8, 20] }, price: 200, desc: '蕴含灵力的剑' },
  { id: 'azure_sword', name: '青冥剑', slot: 'weapon', quality: 'mystic', baseStats: { attack: [20, 50] }, price: 500, desc: '剑身青冥' },
  { id: 'dragon_slayer', name: '斩龙剑', slot: 'weapon', quality: 'earth', baseStats: { attack: [50, 100] }, price: 0, desc: '曾斩蛟龙' },
  { id: 'god_slayer', name: '诛仙剑', slot: 'weapon', quality: 'heaven', baseStats: { attack: [100, 200] }, price: 0, desc: '可诛仙神' },

  // === 副手 ===
  { id: 'wood_shield', name: '木盾', slot: 'offhand', quality: 'common', baseStats: { defense: [2, 5] }, price: 40, desc: '木制小盾' },
  { id: 'iron_shield', name: '铁盾', slot: 'offhand', quality: 'spirit', baseStats: { defense: [5, 15] }, price: 150, desc: '精铁盾牌' },
  { id: 'spirit_shield', name: '灵盾', slot: 'offhand', quality: 'mystic', baseStats: { defense: [15, 40] }, price: 400, desc: '灵力护盾' },

  // === 戒指 ===
  { id: 'copper_ring', name: '铜戒', slot: 'ring1', quality: 'common', baseStats: { hp: [5, 15] }, price: 50, desc: '普通铜戒' },
  { id: 'jade_ring', name: '玉戒', slot: 'ring1', quality: 'spirit', baseStats: { hp: [15, 40], mp: [10, 25] }, price: 200, desc: '温润的玉戒' },
  { id: 'dragon_ring', name: '龙戒', slot: 'ring1', quality: 'mystic', baseStats: { hp: [40, 100], attack: [5, 15] }, price: 550, desc: '龙魂灌注的戒指' },
  { id: 'cosmic_ring', name: '乾坤戒', slot: 'ring1', quality: 'heaven', baseStats: { hp: [120, 280], attack: [15, 40] }, price: 0, desc: '内含小世界' },

  // === 腰带（储物） ===
  { id: 'cloth_belt', name: '布腰带', slot: 'waist', quality: 'common', baseStats: {}, price: 30, capacity: 6, desc: '最基础的腰带' },
  { id: 'storage_pouch', name: '储物袋', slot: 'waist', quality: 'spirit', baseStats: {}, price: 100, capacity: 12, desc: '储物法宝' },
  { id: 'spirit_belt', name: '灵兽袋', slot: 'waist', quality: 'mystic', baseStats: {}, price: 300, capacity: 24, desc: '更大的空间' },
  { id: 'qiankun_bag', name: '乾坤袋', slot: 'waist', quality: 'heaven', baseStats: {}, price: 0, capacity: 60, desc: '内有乾坤' },
];
